@if (failed) {
  <p class="page muted">{{ 'quotes.not_found' | translate }}</p>
}
@if (quote; as q) {
  <div class="pinned">
    <div class="title">
      <a nbButton ghost size="small" [routerLink]="['/opportunities', q.Opportunity_id]" [attr.aria-label]="'quotes.back' | translate"><nb-icon icon="arrow-back-outline"></nb-icon></a>
      <h1>{{ 'quotes.quote' | translate }} {{ q.number }}</h1>
    </div>
    <div class="buttons">
      <button nbButton size="small" shape="semi-round" status="danger" ghost class="delete" [disabled]="busy" (click)="remove()"><nb-icon icon="trash-2-outline"></nb-icon><span>{{ 'quotes.delete' | translate }}</span></button>
      <button nbButton size="small" shape="semi-round" status="basic" [disabled]="busy || !isDirty" (click)="save()">{{ 'save' | translate }}</button>
      <button nbButton size="small" shape="semi-round" status="primary" [disabled]="busy || !!unpriced.length"
              [title]="unpriced.length ? ('quotes.pdf_needs_hours' | translate:{names: unpriced.join(', ')}) : ''" (click)="download()">{{ 'quotes.download' | translate }}</button>
    </div>
  </div>

  <div class="page">
    <aside class="summary card" [attr.aria-label]="'quotes.summary' | translate">
      <p class="summary-label">{{ 'quotes.total' | translate }}</p>
      <p class="summary-total">{{ money(totalCents) }}</p>
      <dl>
        <dt>{{ 'quotes.pricing.label' | translate }}</dt><dd>{{ (byHours ? 'quotes.pricing.hours' : 'quotes.pricing.lines') | translate }}</dd>
        <dt>{{ 'quotes.people' | translate }}</dt><dd>{{ q.people.length }}</dd>
        @if (q.start_date || q.end_date) {
          <dt>{{ 'quotes.period' | translate }}</dt>
          <dd>{{ q.start_date | date:'d MMM y':undefined:lang }} – {{ q.end_date | date:'d MMM y':undefined:lang }}</dd>
        }
        <dt>{{ 'quotes.field.expires_on' | translate }}</dt><dd>{{ q.expires_on | date:'d MMM y':undefined:lang }}</dd>
      </dl>
      @if (unpriced.length) {
        <p class="notice" role="status"><nb-icon icon="alert-triangle-outline" status="warning"></nb-icon>{{ 'quotes.pdf_needs_hours' | translate:{names: unpriced.join(', ')} }}</p>
      } @else if (pdfRefusal && !isDirty) {
        <p class="notice" role="alert"><nb-icon icon="alert-triangle-outline" status="warning"></nb-icon>{{ pdfRefusal }}</p>
      }
    </aside>

    <div class="main">
      <div class="pair">
        <section class="card">
          <h2><nb-icon icon="file-text-outline"></nb-icon><span>{{ 'quotes.section.header' | translate }}</span></h2>
          <div class="fields">
            <div class="field wide">
              <label for="quote-title">{{ 'quotes.field.title' | translate }}<span class="required">*</span></label>
              <input nbInput fullWidth id="quote-title" fieldSize="small" maxlength="255" [status]="errors.title ? 'danger' : 'basic'" [(ngModel)]="q.title">
              @if (error('title'); as m) { <span class="error">{{ m }}</span> }
            </div>
            <div class="field">
              <label for="quote-date">{{ 'quotes.field.quote_date' | translate }}<span class="required">*</span></label>
              <input nbInput fullWidth id="quote-date" type="date" fieldSize="small" [status]="errors.quote_date ? 'danger' : 'basic'" [(ngModel)]="q.quote_date">
              @if (error('quote_date'); as m) { <span class="error">{{ m }}</span> }
            </div>
            <div class="field">
              <label for="quote-expires">{{ 'quotes.field.expires_on' | translate }}<span class="required">*</span></label>
              <input nbInput fullWidth id="quote-expires" type="date" fieldSize="small" [status]="errors.expires_on ? 'danger' : 'basic'" [(ngModel)]="q.expires_on">
              @if (error('expires_on'); as m) { <span class="error">{{ m }}</span> }
            </div>
            <div class="field">
              <label for="quote-responsible">{{ 'quotes.field.responsible' | translate }}</label>
              <nb-select fullWidth id="quote-responsible" size="small" [(selected)]="q.responsible_User_id" [placeholder]="'quotes.none' | translate">
                <nb-option [value]="null">{{ 'quotes.none' | translate }}</nb-option>
                @for (m of members; track m.id) { <nb-option [value]="m.id">{{ m.name_first }} {{ m.name_last }}</nb-option> }
              </nb-select>
            </div>
            <div class="field">
              <span class="label">{{ 'quotes.field.language' | translate }}</span>
              <span class="value">{{ 'quotes.settings.locale.' + q.locale | translate }}</span>
            </div>
          </div>
        </section>

        <section class="card">
          <h2><nb-icon icon="person-outline"></nb-icon><span>{{ 'quotes.section.recipient' | translate }}</span></h2>
          <div class="fields">
            <div class="field wide">
              <label for="quote-recipient">{{ 'quotes.field.recipient_name' | translate }}<span class="required">*</span></label>
              <input nbInput fullWidth id="quote-recipient" fieldSize="small" maxlength="255" [status]="errors.recipient_name ? 'danger' : 'basic'" [(ngModel)]="q.recipient_name">
              @if (error('recipient_name'); as m) { <span class="error">{{ m }}</span> }
            </div>
            <div class="field wide">
              <label for="quote-address">{{ 'quotes.field.recipient_address' | translate }}</label>
              <textarea nbInput fullWidth id="quote-address" cdkTextareaAutosize cdkAutosizeMinRows="2" maxlength="1000" [(ngModel)]="q.recipient_address"></textarea>
            </div>
            <div class="field wide">
              <label for="quote-addressee">{{ 'quotes.field.addressee' | translate }}</label>
              <input nbInput fullWidth id="quote-addressee" fieldSize="small" maxlength="255" [(ngModel)]="q.addressee">
            </div>
            <div class="field wide">
              <label for="quote-salutation">{{ 'quotes.field.salutation' | translate }}</label>
              <input nbInput fullWidth id="quote-salutation" fieldSize="small" maxlength="255" [(ngModel)]="q.salutation">
            </div>
          </div>
        </section>
      </div>

      <section class="card">
        <div class="section-head">
          <h2><nb-icon icon="people-outline"></nb-icon><span>{{ 'quotes.section.staffing' | translate }}</span></h2>
          <div class="segmented" role="group" [attr.aria-label]="'quotes.pricing.label' | translate">
            <button type="button" [attr.aria-pressed]="byHours" [class.active]="byHours" (click)="q.pricing = 'hours'">{{ 'quotes.pricing.hours' | translate }}</button>
            <button type="button" [attr.aria-pressed]="!byHours" [class.active]="!byHours" (click)="q.pricing = 'lines'">{{ 'quotes.pricing.lines' | translate }}</button>
          </div>
        </div>
        <div class="fields period">
          <div class="field">
            <label for="quote-start">{{ 'quotes.field.start_date' | translate }}</label>
            <input nbInput fullWidth id="quote-start" type="date" fieldSize="small" [status]="errors.start_date ? 'danger' : 'basic'" [(ngModel)]="q.start_date">
            @if (error('start_date'); as m) { <span class="error">{{ m }}</span> }
          </div>
          <div class="field">
            <label for="quote-end">{{ 'quotes.field.end_date' | translate }}</label>
            <input nbInput fullWidth id="quote-end" type="date" fieldSize="small" [status]="errors.end_date ? 'danger' : 'basic'" [(ngModel)]="q.end_date">
            @if (error('end_date'); as m) { <span class="error">{{ m }}</span> }
          </div>
        </div>

        <div class="scroll">
          <table class="people" [class.priced]="byHours">
            <thead><tr>
              <th>{{ 'quotes.person' | translate }}</th>
              @if (byHours) { <th class="num rate">{{ 'quotes.rate' | translate }}</th><th class="num hours">{{ 'quotes.hours' | translate }}</th><th class="num amount">{{ 'quotes.amount' | translate }}</th> }
              <th class="act"><span class="sr-only">{{ 'quotes.remove' | translate }}</span></th>
            </tr></thead>
            <tbody>
              @for (p of q.people; track p.User_id; let i = $index) {
                <tr>
                  <td class="name">{{ name(p) }}</td>
                  @if (byHours) {
                    <td class="num rate" [attr.data-label]="'quotes.rate' | translate"><input nbInput fullWidth inputmode="decimal" fieldSize="small" [status]="personCents(p) === null ? 'warning' : 'basic'" [attr.aria-label]="('quotes.rate' | translate) + ' ' + name(p)" [ngModel]="decimal(p.rate)" (ngModelChange)="p.rate = typedDecimal($event)"></td>
                    <td class="num hours" [attr.data-label]="'quotes.hours' | translate"><input nbInput fullWidth inputmode="decimal" fieldSize="small" [status]="personCents(p) === null ? 'warning' : 'basic'" [attr.aria-label]="('quotes.hours' | translate) + ' ' + name(p)" [ngModel]="decimal(p.hours)" (ngModelChange)="p.hours = typedDecimal($event)"></td>
                    <td class="num amount" [attr.data-label]="'quotes.amount' | translate">{{ money(personCents(p)) || '–' }}</td>
                  }
                  <td class="act"><button nbButton ghost size="small" type="button" [attr.aria-label]="('quotes.remove' | translate) + ' ' + name(p)" (click)="removePerson(i)"><nb-icon icon="trash-2-outline"></nb-icon></button></td>
                </tr>
              }
            </tbody>
            @if (byHours) {
              <tfoot><tr><td colspan="3">{{ 'quotes.total' | translate }}</td><td class="num amount">{{ money(totalCents) }}</td><td></td></tr></tfoot>
            }
          </table>
        </div>
        @if (error('people'); as m) { <p class="error">{{ m }}</p> }
        @if (candidates.length) {
          <nb-select class="add-person" size="small" [placeholder]="'quotes.add_person' | translate" [(selected)]="adding" (selectedChange)="addPerson($event)">
            @for (m of candidates; track m.id) { <nb-option [value]="m.id">{{ m.name_first }} {{ m.name_last }}</nb-option> }
          </nb-select>
        }

        @if (!byHours) {
          <h3>{{ 'quotes.price_lines' | translate }}</h3>
          <div class="scroll">
            <table class="lines">
              <thead><tr><th>{{ 'quotes.description' | translate }}</th><th class="num amount">{{ 'quotes.amount' | translate }}</th><th class="act"><span class="sr-only">{{ 'quotes.remove' | translate }}</span></th></tr></thead>
              <tbody>
                @for (l of q.price_lines; track $index; let i = $index) {
                  <tr>
                    <td><input nbInput fullWidth fieldSize="small" maxlength="500" [attr.aria-label]="'quotes.description' | translate" [status]="errors['price_lines.' + i + '.description'] ? 'danger' : 'basic'" [(ngModel)]="l.description"></td>
                    <td class="num"><input nbInput fullWidth inputmode="decimal" fieldSize="small" [attr.aria-label]="'quotes.amount' | translate" [status]="errors['price_lines.' + i + '.amount'] ? 'danger' : 'basic'" [ngModel]="decimal(l.amount)" (ngModelChange)="l.amount = typedDecimal($event)"></td>
                    <td class="act"><button nbButton ghost size="small" type="button" [attr.aria-label]="'quotes.remove' | translate" (click)="removeLine(i)"><nb-icon icon="trash-2-outline"></nb-icon></button></td>
                  </tr>
                }
              </tbody>
              <tfoot><tr><td>{{ 'quotes.total' | translate }}</td><td class="num amount">{{ money(totalCents) }}</td><td></td></tr></tfoot>
            </table>
          </div>
          <button nbButton ghost size="small" status="primary" type="button" class="add-line" (click)="addLine()"><nb-icon icon="plus-outline"></nb-icon>{{ 'quotes.add_line' | translate }}</button>
        }
      </section>

      <section class="card texts">
        <h2><nb-icon icon="edit-2-outline"></nb-icon><span>{{ 'quotes.section.texts' | translate }}</span></h2>
        <label for="quote-description">{{ 'quotes.field.description' | translate }}</label>
        <textarea nbInput fullWidth id="quote-description" cdkTextareaAutosize cdkAutosizeMinRows="2" maxlength="20000" [(ngModel)]="q.description"></textarea>
        @for (slot of slots; track slot) {
          <label [for]="'quote-text-' + slot">{{ 'quotes.text.' + slot | translate }}</label>
          <textarea nbInput fullWidth cdkTextareaAutosize cdkAutosizeMinRows="2" maxlength="20000" [id]="'quote-text-' + slot" [(ngModel)]="q.texts[slot]"></textarea>
        }
      </section>
    </div>
  </div>
}
